import React, {useEffect, useState, useRef} from 'react';
import { Button } from '../ui/button';
import { Brain, X } from 'lucide-react';
import { Slider } from '../ui/slider';
import { CHAT_CONFIG } from '@/constants/chat.constant';
import { useChatState } from '@/hooks/useChatState';

/**
 * Props for the ReasoningEffort component
 */
interface ReasoningEffortProps {
  /** Current reasoning effort value */
  effort: number;
  /** Callback function called when reasoning effort changes */
  onEffortChange: (value: number) => void;
  /** Currently selected model (affects minimum effort limit) */
  selectedModel: any;
}

/**
 * ReasoningEffort component displays a dropdown button with a slider to control
 * the reasoning effort level for AI responses. Some models have a minimum effort
 * limit (e.g., 2). The dropdown shows labels: Minimal, Low, Medium, High.
 * 
 * @component
 * @param {ReasoningEffortProps} props - Component props
 * @param {number} props.effort - Current reasoning effort value
 * @param {(value: number) => void} props.onEffortChange - Callback fired when effort changes
 * @param {any} props.selectedModel - Selected AI model
 * @returns {JSX.Element} Button with dropdown containing effort slider
 * 
 * @example
 * ```tsx
 * <ReasoningEffort 
 *   effort={3} 
 *   onEffortChange={setEffort}
 *   selectedModel="gpt-4"
 * />
 * ```
 */
export const ReasoningEffort :React.FC<ReasoningEffortProps> = ({
  effort,
  onEffortChange,
  selectedModel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(effort);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [limitMinimal, setLimitMinimal] = useState(false);

  // Sync when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSliderValue(effort);
    }
  }, [isOpen]);

  useEffect(() => {
    CHAT_CONFIG.REASONING_EFFORT.LOW_LIMIT_REASONING_MODELS.includes(selectedModel) ? setLimitMinimal(true) : setLimitMinimal(false)
  }, [selectedModel])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSliderChange = (value: number[]) => {
    const newValue = value[0];
    if (limitMinimal && newValue < 2) {
      setSliderValue(2);
      onEffortChange(2);
    } else {
      setSliderValue(newValue);
      onEffortChange(newValue);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={isOpen ? "default" : "outline"}
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Temperature settings"
        aria-expanded={isOpen}
        className={`rounded-full ${
          isOpen
            ? "bg-[#005587] hover:bg-[#004070] text-white"
            : "border-[#D4D4D8] text-[#005587] hover:text-[#005587] hover:bg-gray-50"
        }`}
      >
        <Brain size={16} />
      </Button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-[#09090B] text-sm">
                Reasoning effort settings
              </p>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                aria-label="Close reasoning effort settings"
                className="h-6 w-6 rounded-full hover:bg-gray-100"
              >
                <X size={14} className="text-gray-500" />
              </Button>
            </div>

            <div className="space-y-3">
              <Slider
                value={[sliderValue]}
                onValueChange={handleSliderChange}
                min={limitMinimal ? 2 : CHAT_CONFIG.REASONING_EFFORT.MIN}
                max={CHAT_CONFIG.REASONING_EFFORT.MAX}
                step={CHAT_CONFIG.REASONING_EFFORT.STEP}
                className="w-full cursor-pointer"
                aria-label="Reasoning effort slider"
              />
              <div className="flex justify-between text-xs text-[#71717B]">
                {!limitMinimal && <span>Minimal</span>}
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};